import doWhen from "@gef-ui/utils/doWhen";
import parseInt10 from "@gef-ui/utils/parseInt10";
import invariant from 'invariant';
import PropTypes from 'prop-types';
import __ from "ramda/es/__";
import always from "ramda/es/always";
import any from "ramda/es/any";
import compose from "ramda/es/compose";
import filter from "ramda/es/filter";
import gt from "ramda/es/gt";
import isEmpty from "ramda/es/isEmpty";
import length from "ramda/es/length";
import map from "ramda/es/map";
import mapAccum from "ramda/es/mapAccum";
import not from "ramda/es/not";
import o from "ramda/es/o";
import propOr from "ramda/es/propOr";
import range from "ramda/es/range";
import replace from "ramda/es/replace";
import slice from "ramda/es/slice";
import sum from "ramda/es/sum";
import update from "ramda/es/update";
import cx from "ramda-extension/es/cx";
import isNotNil from "ramda-extension/es/isNotNil";
import React, { cloneElement, Component, createElement } from 'react';
import { findDOMNode } from 'react-dom';
import ResizeObserver from 'resize-observer-polyfill';
import Hamburger from '../../../atoms/SvgIcon/Icons/Hamburger';
import { withDefaultComponentProps } from '../../../decorators/withDefaultComponentProps';
import filterChildren from '../../../utils/filterChildren';
import MenuDivider from '../MenuDivider';
import MenuItem from '../MenuItem';
const COMPONENT_NAME = 'Menu';
const OVERFLOW_TOLERANCE = 10;
const filterEmptyChildren = filterChildren(isNotNil);
const replacePx = replace('px', '');
const mapMargin = (computedStyle)=>map(compose(parseInt10, replacePx, propOr('', __, computedStyle)));
const createBlankMenuItems = compose(map(always(0)), range(0), length);
const updateMenuItemsWidth = (index, offsetWidth)=>(oldState)=>({
            menuItemsWidth: update(index, offsetWidth, oldState.menuItemsWidth)
        });
class Menu extends Component {
    constructor(props){
        super(props);
        this.resizeObserver = new ResizeObserver(()=>{
            window.requestAnimationFrame(()=>{
                this.handleWindowSize();
            });
        });
        this.state = {
            menuRealWidth: 0,
            menuItemsWidth: o(createBlankMenuItems, filterEmptyChildren, props.children)
        };
    }
    static getDerivedStateFromProps(props, state) {
        const newChildren = filterEmptyChildren(props.children);
        if (state.menuItemsWidth.length !== newChildren.length) {
            return Object.assign({}, state, {
                menuItemsWidth: createBlankMenuItems(newChildren)
            });
        } else {
            return null;
        }
    }
    componentDidMount() {
        doWhen(this.props.horizontal, ()=>{
            this.handleWindowSize();
            this.resizeObserver.observe(this._menu);
        });
    }
    componentWillUnmount() {
        doWhen(this.props.horizontal, ()=>this.resizeObserver.unobserve(this._menu));
    }
    handleWindowSize = ()=>{
        if (this._menu) {
            let menuRealWidth = Math.ceil(this._menu.getBoundingClientRect().width);
            const menuComputedStyle = window.getComputedStyle(this._menu);
            if (menuComputedStyle.display === 'flex') {
                menuRealWidth += o(sum, mapMargin(menuComputedStyle))([
                    'marginLeft',
                    'marginRight'
                ]);
            }
            this.setState(()=>({
                    menuRealWidth
                }));
        }
    };
    countWeightedItems = ()=>{
        const moreItemSize = this._moreItemWidth ? this._moreItemWidth : 0;
        return mapAccum((a, b)=>[
                a + b,
                a + b
            ], OVERFLOW_TOLERANCE + moreItemSize, this.state.menuItemsWidth)[1];
    };
    handleMenuItemClick = (event, item, index)=>{
        this.props.onItemClick?.(event, item, index);
        item.props.onClick?.(event);
        // dirty fix for nested children. We should rewrite menu to use context in future.
        item?.props?.children?.props?.onClick?.(event);
    };
    cloneMenuItem = (child, childIndex)=>/*#__PURE__*/ cloneElement(child, {
            ref: (menuItem)=>doWhen(this.props.horizontal && menuItem, ()=>{
                    invariant(this.state.menuItemsWidth[childIndex] !== undefined, 'something is wrong, menu does not have index');
                    const menuNode = findDOMNode(menuItem);
                    const offsetWidth = menuNode ? menuNode.offsetWidth : 0;
                    let marginsWidth = 0;
                    if (menuNode) {
                        const style = window.getComputedStyle(menuNode);
                        marginsWidth = (parseFloat(style.marginRight) || 0) + (parseFloat(style.marginLeft) || 0);
                    }
                    const completeWidth = offsetWidth + marginsWidth;
                    if (this.state.menuItemsWidth[childIndex] !== completeWidth) {
                        this.setState(updateMenuItemsWidth(childIndex, completeWidth));
                    }
                }),
            onClick: (event)=>this.handleMenuItemClick(event, child, childIndex),
            subMenuLevel: this.props.subMenuLevel
        });
    filterAndCloneChildren = ()=>React.Children.map(filterEmptyChildren(this.props.children), compose((child)=>/*#__PURE__*/ React.cloneElement(child, {
                useList: this.props.useList
            }), (child, index)=>child.type.displayName === MenuDivider.displayName ? child : this.cloneMenuItem(child, index)));
    renderMenu = (...children)=>{
        const { className, horizontal, onItemClick, subMenuLevel, useList, overflowMenuItemIcon, overflowMenuItemLabel, ...other } = this.props;
        return /*#__PURE__*/ createElement(useList ? 'ul' : 'div', {
            ref: (menu)=>{
                this._menu = menu;
            },
            className: cx('Menu', {
                'Menu--typeHorizontal': horizontal
            }, className),
            ...other
        }, children);
    };
    renderHorizontalMenu = ()=>{
        const { subMenuLevel, overflowMenuItemIcon, overflowMenuItemLabel, horizontal } = this.props;
        const newChildren = this.filterAndCloneChildren();
        const { menuRealWidth } = this.state;
        if (menuRealWidth > 0) {
            const weightItemsNew = this.countWeightedItems();
            const totalOverflow = isEmpty(weightItemsNew) ? false : o(not, any(gt(menuRealWidth)), weightItemsNew);
            const visibleItemOrder = filter(gt(menuRealWidth), weightItemsNew).length;
            if (visibleItemOrder > 0 || totalOverflow) {
                const visibleChildren = !totalOverflow ? slice(0, visibleItemOrder, newChildren) : null;
                const hiddenChildren = newChildren.length !== visibleItemOrder ? /*#__PURE__*/ React.createElement(MenuItem, {
                    ref: (moreItem)=>{
                        this._moreItemWidth = moreItem ? findDOMNode(moreItem).offsetWidth : null;
                    },
                    className: "ignore-react-onclickoutside",
                    label: overflowMenuItemLabel,
                    icon: overflowMenuItemIcon,
                    typeSelect: true,
                    subMenuLevel: subMenuLevel,
                    key: "overflowItem",
                    horizontalToggle: horizontal
                }, /*#__PURE__*/ React.createElement(Menu, null, totalOverflow ? newChildren : slice(visibleItemOrder, Infinity, newChildren))) : null;
                return this.renderMenu(visibleChildren, hiddenChildren);
            }
        }
        return this.renderMenu(newChildren);
    };
    render() {
        return this.props.horizontal ? this.renderHorizontalMenu() : this.renderMenu(this.filterAndCloneChildren());
    }
}
Menu.propTypes = {
    /**
	 * Children will be rendered within the `Menu`.
	 */ children: PropTypes.node,
    /**
	 * Custom `className` for the root.
	 */ className: PropTypes.string,
    /**
	 * If true, menu will have horizontal class
	 */ horizontal: PropTypes.bool,
    /**
	 * On item click function
	 *
	 * @param {Object} event - event targeting the element
	 * @param {Node} item - item node
	 * @param {Number} index - index of item
	 * @return {undefined} Nothing
	 */ onItemClick: PropTypes.func,
    /**
	 * Overflow MenuItem icon
	 */ overflowMenuItemIcon: PropTypes.node,
    /**
	 * Overflow MenuItem label
	 */ overflowMenuItemLabel: PropTypes.string,
    /**
	 * Level of submenu, no need to set
	 */ subMenuLevel: PropTypes.number,
    /**
	 * Whether to use `ul` and `li` elements
	 */ useList: PropTypes.bool
};
export { Menu };
export default withDefaultComponentProps(COMPONENT_NAME, {
    subMenuLevel: 0,
    overflowMenuItemLabel: '',
    overflowMenuItemIcon: /*#__PURE__*/ React.createElement(Hamburger, null)
}, Menu);
